import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Heart, Briefcase, Users, UserX, Monitor, ArrowRight, ArrowLeft, Loader2 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'Infedeltà Coniugale': Heart,
  'Marital Infidelity': Heart,
  'Infedeltà Lavorativa': Briefcase,
  'Workplace Infidelity': Briefcase,
  'Concorrenza Sleale': Users,
  'Unfair Competition': Users,
  'Bullismo e Cyber Bullismo': UserX,
  'Bullying and Cyber Bullying': UserX,
};

export default function InvestigationTypeSelect() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    const privacyAccepted = sessionStorage.getItem('privacyAccepted');
    if (!privacyAccepted) {
      navigate('/privacy');
      return;
    }
    fetchTypes();
  }, [navigate]);

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/investigation-types`);
      setTypes(response.data);
    } catch (error) {
      toast.error(t('error'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (type) => {
    setSelectedType(type);
  };

  const handleProceed = () => {
    if (selectedType) {
      sessionStorage.setItem('selectedType', JSON.stringify(selectedType));
      navigate('/questionnaire');
    }
  };

  const getIcon = (name) => {
    const Icon = iconMap[name] || Monitor;
    return Icon;
  };

  if (loading) {
    return (
      <CyberLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-neon-green animate-spin" />
        </div>
      </CyberLayout>
    );
  }

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-12 fade-in-up">
            <h1 
              className="font-orbitron text-3xl sm:text-4xl font-bold text-white mb-2 tracking-wider"
              data-testid="select-type-title"
            >
              {t('selectType')}
            </h1>
            <p className="font-mono text-neon-cyan text-sm tracking-widest uppercase">
              {t('selectTypeSubtitle')}
            </p>
          </div>

          {/* Types Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {types.map((type, index) => {
              const Icon = getIcon(type[`name_${language}`] || type.name_it);
              const isSelected = selectedType?.id === type.id;
              
              return (
                <button
                  key={type.id}
                  onClick={() => handleSelect(type)}
                  data-testid={`type-card-${index}`}
                  className={`cyber-card rounded-lg p-6 text-left transition-all duration-300 fade-in-up ${
                    isSelected 
                      ? 'border-neon-green shadow-neon' 
                      : 'hover:border-neon-green/50'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-4 ${
                    isSelected ? 'bg-neon-green text-black' : 'bg-cyber-surface text-neon-green'
                  }`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  
                  <h3 className="font-orbitron text-lg font-semibold text-white mb-2">
                    {type[`name_${language}`] || type.name_it}
                  </h3>
                  
                  <p className="font-mono text-xs text-gray-400 leading-relaxed">
                    {type[`description_${language}`] || type.description_it}
                  </p>

                  {isSelected && (
                    <div className="mt-4 flex items-center gap-2 text-neon-green font-mono text-xs">
                      <div className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
                      SELECTED
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: '0.5s' }}>
            <button
              onClick={() => navigate('/privacy')}
              className="cyber-btn-outline px-6 py-3 flex items-center justify-center gap-2 rounded-md flex-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('back')}</span>
            </button>
            
            <button
              onClick={handleProceed}
              disabled={!selectedType}
              data-testid="proceed-questionnaire-btn"
              className={`cyber-btn px-6 py-3 flex items-center justify-center gap-2 flex-1 ${
                !selectedType ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <span>{t('next')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
